import { z } from 'zod'

// ── Document reference (ADR-011 object storage) ──────────────────────────────
//
// Metadata only. `ocrSubVerdictSchema` lived here for `docRefSchema` and
// `ocrEvidenceResponseSchema`; both consumers went with the OCR surface (SG#7)
// and the columns behind them were dropped by migration 0187 (SG#12).

export const docRefSchema = z.object({
  id: z.guid(),
  filename: z.string(),
  mimeType: z.enum(['image/jpeg', 'image/png', 'image/webp', 'application/pdf']),
  byteSize: z.number().int().nonnegative(),
  uploadedAt: z.string().datetime(),
})

export type DocRef = z.infer<typeof docRefSchema>

// ─────────────────────────────────────────────────────────────────────────────

export const leaveTypeIdSchema = z.enum([
  'annual',
  'medical',
  'hospitalization',
  'birthday',
  'compassionate',
  'maternity',
  'paternity',
  'marriage',
  // Retired 2026 — the old combined "Marriage & Family Care" quota. History
  // only: never applyable, no dashboard tile, no annual reset.
  'marriage_family_care',
  'unpaid',
  'replacement',
])

export type LeaveTypeId = z.infer<typeof leaveTypeIdSchema>

export const leaveTypeSchema = z.object({
  id: leaveTypeIdSchema,
  label: z.string(),
  color: z.string(),
  halfDayAllowed: z.boolean(),
  documentRequired: z.boolean(),
  description: z.string(),
  defaultEntitlement: z.number().int().nullable(),
  fixedDurationDays: z.number().int().nullable().default(null),
})

export type LeaveType = z.infer<typeof leaveTypeSchema>

export const leaveStatusSchema = z.enum([
  'draft',
  // HR-first review holding state (migration 0195, feature HR_DOCUMENT_REVIEW).
  // A document-required leave sits here — in the HR review queue, invisible to
  // the manager — until HR forwards it (→ `pending`, normal approver flow),
  // rejects it (→ `rejected`), or asks the employee for the document. HR never
  // appears in the approver-facing path.
  'pending_hr_review',
  'pending',
  // RFC 007 — the single "escalated to the Exec Director" state. Escalation
  // terminates only at the Exec Director now; the legacy multi-tier composite
  // states (needs_review, pending_manager_recommendation,
  // pending_director_decision) were removed.
  'escalated',
  'approved',
  'rejected',
  'cancelled',
  // Cancellation-of-approved-leave holding state (migration 0138). An
  // approved leave whose owner asked to cancel it sits here — still on the
  // books — until their manager approves the cancellation (→ `cancelled`) or
  // rejects it (→ back to `approved`).
  'pending_cancellation',
])

export type LeaveStatus = z.infer<typeof leaveStatusSchema>

export const escalationTargetSchema = z.enum(['manager', 'hod', 'director', 'exec_director'])
export type EscalationTarget = z.infer<typeof escalationTargetSchema>

export const leaveSchema = z.object({
  id: z.guid(),
  employeeId: z.guid(),
  type: leaveTypeIdSchema,
  startDate: z.string().date(),
  endDate: z.string().date(),
  halfDay: z.boolean(),
  /**
   * Side-specific half-day flags. For multi-day requests each one
   * deducts 0.5 from the working-day count. Optional in the schema
   * so legacy mock fixtures keep validating; new wire payloads always
   * carry them. Consumers should treat `undefined` as `false`.
   */
  halfStart: z.boolean().optional(),
  halfEnd: z.boolean().optional(),
  /**
   * Server-computed billable day count (calendar span − weekends − public
   * holidays − half-day slots; elapsed-day types count every calendar day).
   * The web can't subtract holidays itself, so list surfaces should render
   * this when present. Optional — absent on responses that don't compute it,
   * where consumers fall back to a client-side weekday count.
   */
  days: z.number().optional(),
  /**
   * Per-date half-day slots (migration 0057). Each entry represents a
   * working day the employee marked as AM or PM. Empty array when no
   * half-days are selected. Optional for back-compat with legacy records.
   */
  halfDays: z.array(z.object({ date: z.string().date(), slot: z.enum(['AM', 'PM']) })).optional(),
  status: leaveStatusSchema,
  reason: z.string(),
  rejectReason: z.string().nullable(),
  // Reason the requester gave when asking to cancel an approved leave.
  // Non-null while status === 'pending_cancellation'; surfaced on the
  // manager's decision card. Optional for back-compat with legacy fixtures.
  cancelReason: z.string().nullable().optional(),
  escalationTarget: escalationTargetSchema.nullable(),
  isEmergency: z.boolean(),
  // True when HR encoded this leave out of system (bypassing the submit gates).
  // Optional for back-compat with legacy fixtures + producers; the API always
  // sends it (LeaveOut defaults to false).
  isOutOfSystem: z.boolean().optional(),
  // True when this leave was approved while it put the team over its
  // concurrent-absence cap (capacity de-gated 2026-09-24). Drives the "Over
  // capacity" row badge across manager / HR / employee list surfaces. Optional
  // for back-compat; the API always sends it (LeaveOut defaults to false).
  approvedOverCapacity: z.boolean().optional(),
  documents: z.array(docRefSchema),
  expiry: z.string().date().nullable(),
  decidedById: z.guid().nullable(),
  decidedAt: z.string().datetime().nullable(),
  slackMessageTs: z.string().nullable(),
  slackChannelId: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  // Overflow composite fields — slice 4a (2026-05-13).
  // Points at the linked sibling row (annual ↔ unpaid) when this row was
  // created via apply-with-overflow. NULL for normal single-row leaves.
  linkedRequestId: z.guid().nullable().optional(),
  // Pending-state reason (migration 0088). Effectively always NULL now (the
  // blackout reconciler that stamped "blackout_reset" was removed); retained
  // for the BlackoutResetBanner until that flow is retired.
  pendingReason: z.string().nullable().optional(),
  // Manager's note captured when they escalate a request to the Exec Director
  // (migration 0089, RFC 007). NULL on every leave that wasn't escalated.
  managerJustification: z.string().nullable().optional(),
})

export type Leave = z.infer<typeof leaveSchema>

/** Per-date half-day slot entry (migration 0057). */
export const halfDaySlotSchema = z.object({
  date: z.string().date(),
  slot: z.enum(['AM', 'PM']),
})
export type HalfDaySlot = z.infer<typeof halfDaySlotSchema>

export const leaveCreateSchema = leaveSchema
  .omit({
    id: true,
    status: true,
    rejectReason: true,
    escalationTarget: true,
    decidedById: true,
    decidedAt: true,
    slackMessageTs: true,
    slackChannelId: true,
    createdAt: true,
    updatedAt: true,
    // ADR-011: documents are uploaded after leave creation via
    // POST /api/v1/leaves/:id/documents — not included in the create body.
    documents: true,
    // halfDays not on leaveSchema yet — added in extend below.
    halfDays: true,
  })
  .extend({
    halfDay: z.boolean().default(false),
    // Optional with no default so mock fixtures and legacy callers can
    // omit them; the server defaults missing fields to false.
    halfStart: z.boolean().optional(),
    halfEnd: z.boolean().optional(),
    // Per-date half-day list (migration 0057). Optional for back-compat;
    // when provided the server uses it for duration maths and derives
    // halfStart / halfEnd from it automatically.
    halfDays: z.array(halfDaySlotSchema).optional(),
    isEmergency: z.boolean().default(false),
    // When submitting from a restored draft, the draft leave's id. The api
    // re-points documents attached to the draft (uploaded during Save-draft,
    // with their OCR verdict) onto the new leave so they carry over.
    fromDraftId: z.guid().optional(),
  })
  .refine((d) => d.startDate <= d.endDate, {
    message: 'startDate must be on or before endDate',
    path: ['endDate'],
  })

export type LeaveCreate = z.infer<typeof leaveCreateSchema>

export const leaveDecisionSchema = z.discriminatedUnion('decision', [
  z.object({ decision: z.literal('approve') }),
  z.object({ decision: z.literal('reject'), reason: z.string().min(1) }),
])

export type LeaveDecision = z.infer<typeof leaveDecisionSchema>

export const leaveRejectBodySchema = z.object({
  reason: z.string().min(1).max(500),
})
export type LeaveRejectBody = z.infer<typeof leaveRejectBodySchema>

export const leaveEscalateBodySchema = z.object({
  target: escalationTargetSchema,
})
export type LeaveEscalateBody = z.infer<typeof leaveEscalateBodySchema>

export const hrCancelLeaveRequestSchema = z.object({
  reason: z.string().min(10).max(2000),
})
export type HrCancelLeaveRequest = z.infer<typeof hrCancelLeaveRequestSchema>


// Wave 3 predictive chip schemas — populated server-side for the manager
// queue context only (status=pending, scope=team). All three are
// optional + nullable: older responses won't carry them; non-manager
// contexts always omit them.
export const blackoutProximitySchema = z.object({
  name: z.string(),
  daysUntil: z.number().int(),
})
export type BlackoutProximity = z.infer<typeof blackoutProximitySchema>

export const clusterImpactSchema = z.object({
  date: z.string().date(),
  headcount: z.number().int(),
  slotsTotal: z.number().int(),
})
export type ClusterImpact = z.infer<typeof clusterImpactSchema>

export const holidayAdjacencySchema = z.object({
  holidayName: z.string(),
  position: z.enum(['before', 'after', 'straddles']),
})
export type HolidayAdjacency = z.infer<typeof holidayAdjacencySchema>

// Policy v1.0 §2.4 — health-bar projection for the requesting employee.
// Embedded on every manager-queue LeaveListItem so the request card can
// colour by the outlook band (Red HIGH / Amber MEDIUM — LOW renders
// quiet, no chip). Populated only for the pending manager queue; null
// elsewhere (employee /me/leaves, historical statuses, HR scope=all).
export const healthBarSummarySchema = z.object({
  score: z.number().int().nonnegative(),
  band: z.enum(['low', 'medium', 'high']),
  contributingPatterns: z.array(z.string()).default([]),
})
export type HealthBarSummary = z.infer<typeof healthBarSummarySchema>

export const leaveListItemSchema = leaveSchema.extend({
  employee: z.object({
    id: z.guid(),
    name: z.string(),
    preferredName: z.string().nullable(),
    teamId: z.guid(),
    // Identity context for the manager request card title + pills.
    // Optional/nullable — older API responses omit them.
    jobTitle: z.string().nullable().optional(),
    employmentType: z.enum(['full_time', 'contractual']).nullable().optional(),
    onProbation: z.boolean().optional(),
    contractEndDate: z.string().nullable().optional(),
  }),
  team: z.object({
    id: z.guid(),
    name: z.string(),
  }),
  // No priority score on the list row: the pending queue is ordered
  // first-come-first-serve, so nothing consumes one. The per-rule breakdown
  // lives on the leave *detail* payload (`leave-detail.ts`), where it explains
  // a decision rather than ordering a queue. The advisor status / verdict /
  // reason fields went with the leaves.ai_advisor_* columns in migration 0185.
  // Wave 3 predictive chip fields. See helper in services/api/src/services/manager_queue_chips.py.
  blackoutProximity: blackoutProximitySchema.nullable().optional(),
  clusterImpact: clusterImpactSchema.nullable().optional(),
  holidayAdjacency: holidayAdjacencySchema.nullable().optional(),
  // Policy v1.0 §2.4 — employee health-bar band. Drives the colour chip
  // on the manager request card. Populated only on the pending manager
  // queue; null on other contexts.
  healthBar: healthBarSummarySchema.nullable().optional(),
})
export type LeaveListItem = z.infer<typeof leaveListItemSchema>

